/**
 * Shared page-level (non-sheet) primitives for the rider portal.
 *
 * Companion to sheetPrimitives.tsx — that file owns the modal/bottom-sheet
 * vocabulary; this file owns the chrome that lives on the page itself
 * (app bar, page headers, cards, buttons, segmented controls, etc).
 *
 * Same rules apply:
 *  - Reads riderLight tokens via the r(theme) cast helper.
 *  - Component-only exports (no exported keyframes/types/helpers) so the
 *    file satisfies react-refresh/only-export-components.
 */

import styled, { keyframes, css } from 'styled-components';
import { r } from './riderTheme';

// ─── Animations (internal only) ────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ─── App shell ─────────────────────────────────────────────────────────────

// The full-viewport rider shell — sets the canvas color and ensures the
// content scrolls beneath the fixed app bar.
export const RiderShell = styled.div`
  height: 100vh;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => r(theme).surface.sheetEdge};
  font-family: 'Geist', system-ui, sans-serif;
  font-feature-settings: 'ss01', 'cv11';
  color: ${({ theme }) => r(theme).text.primary};
`;

// Body scroll region. Keep `padding-bottom` clear of the fixed bottom tab bar.
export const ScreenScroll = styled.main`
  flex: 1;
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 16px 16px 96px;
`;

// ─── App bar ───────────────────────────────────────────────────────────────

// Midnight top bar. Left slot = scooter selector; right slot = user profile;
// center = optional title. A thin lime hairline rides the bottom edge.
export const AppBar = styled.header`
  position: relative;
  flex-shrink: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  padding: max(8px, env(safe-area-inset-top)) 12px 8px;
  background: ${({ theme }) => r(theme).cta.bg};
  color: ${({ theme }) => r(theme).text.inverse};
  z-index: 100;
  box-shadow: 0 1px 0 ${({ theme }) => r(theme).surface.edge};

  /* Lime hairline underline. Carries the brand accent into the chrome. */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background: ${({ theme }) =>
      `linear-gradient(90deg,
        transparent 0%,
        ${r(theme).accent.limeDeep} 18%,
        ${r(theme).accent.lime} 50%,
        ${r(theme).accent.limeDeep} 82%,
        transparent 100%
      )`};
    opacity: 0.6;
    pointer-events: none;
  }
`;

export const AppBarLeft = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
`;

export const AppBarCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  text-align: center;
`;

export const AppBarRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
`;

// Short uppercase microtype, sits above the title. Use for the route name
// when the bar is rendered on routes that need a label.
export const AppBarEyebrow = styled.span`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1;
  margin-bottom: 3px;
`;

export const AppBarTitle = styled.h1`
  font-family: 'Geist', sans-serif;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 1.1;
  color: ${({ theme }) => r(theme).text.inverse};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// ─── In-bar chip (used by ScooterSelector + MobileUserProfile triggers) ────

// Small translucent pill that reads correctly on the midnight bar. Exported
// here so both bar slot components share the same affordance.
export const AppBarChip = styled.button.attrs({ type: 'button' as const })`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  min-height: 36px;
  max-width: min(46vw, 200px);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  color: ${({ theme }) => r(theme).text.inverse};
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  cursor: pointer;
  transition: background 160ms ease, border-color 160ms ease, transform 120ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.10);
    border-color: rgba(255, 255, 255, 0.16);
  }
  &:active {
    transform: scale(0.98);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
  }
`;

// ─── Page header (used inside the content area, beneath the app bar) ──────

// A subtle section header that lives at the top of a route's content. Pairs
// a mono eyebrow with a sans-serif title and an optional right-side action.
export const PageHeader = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 2px 14px;
  animation: ${fadeIn} 380ms cubic-bezier(0.2, 0.8, 0.2, 1);
`;

export const PageHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const PageHeaderEyebrow = styled.span`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

export const PageHeaderTitle = styled.h2`
  font-family: 'Geist', sans-serif;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: ${({ theme }) => r(theme).text.primary};
  margin: 0;
`;

export const PageHeaderAccent = styled.span`
  color: ${({ theme }) => r(theme).accent.ok};
`;

// ─── Page card (white surface for grouped content) ──────────────────────────

export const PageCard = styled.section`
  position: relative;
  background: ${({ theme }) => r(theme).surface.sheet};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 16px;
  padding: 18px 18px 16px;
  margin-bottom: 14px;
  animation: ${fadeIn} 380ms cubic-bezier(0.2, 0.8, 0.2, 1);
`;

// ─── Buttons ───────────────────────────────────────────────────────────────

// Midnight pill with optional lime chevron circle on the right. The page-level
// counterpart to the modal CTA — same DNA, smaller height, no animation.
export const PrimaryButton = styled.button.attrs({ type: 'button' as const })<{
  $disabled?: boolean;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 46px;
  padding: 0 18px;
  background: ${({ theme }) => r(theme).cta.bg};
  border: 1px solid ${({ theme }) => r(theme).cta.border};
  border-radius: 12px;
  color: ${({ theme }) => r(theme).cta.text};
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.01em;
  cursor: ${p => (p.$disabled ? 'not-allowed' : 'pointer')};
  transition: background 180ms ease, transform 160ms ease;

  ${p =>
    p.$disabled &&
    css`
      opacity: 0.4;
      filter: saturate(0.6);
    `}

  &:not(:disabled):hover {
    background: ${({ theme }) => r(theme).cta.bgHover};
  }
  &:not(:disabled):active {
    transform: scale(0.99);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
  }
`;

// Outlined, ghost variant. Lives next to PrimaryButton in two-up button rows.
export const GhostButton = styled.button.attrs({ type: 'button' as const })<{
  $disabled?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 46px;
  padding: 0 18px;
  background: transparent;
  border: 1px solid ${({ theme }) => r(theme).surface.edgeStrong};
  border-radius: 12px;
  color: ${({ theme }) => r(theme).text.primary};
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.01em;
  cursor: ${p => (p.$disabled ? 'not-allowed' : 'pointer')};
  transition: background 180ms ease, border-color 180ms ease;

  ${p =>
    p.$disabled &&
    css`
      opacity: 0.4;
    `}

  &:not(:disabled):hover {
    background: rgba(18, 18, 21, 0.04);
    border-color: ${({ theme }) => r(theme).text.primary};
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
  }
`;

// Small lime chevron circle for the right edge of PrimaryButton.
export const ButtonChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${({ theme }) => r(theme).accent.lime};
  color: ${({ theme }) => r(theme).cta.bg};
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 0 0 1px rgba(192, 223, 22, 0.45);
  transition: transform 160ms ease;

  ${PrimaryButton}:not(:disabled):hover & {
    transform: translateX(2px);
  }
`;

// ─── Bottom tab bar (replaces antd-mobile's blue tint) ──────────────────────

export const RiderTabBar = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: ${({ theme }) => r(theme).surface.sheet};
  border-top: 1px solid ${({ theme }) => r(theme).surface.edge};
  padding: 6px 0 max(6px, env(safe-area-inset-bottom));
  z-index: 1000;
`;

export const RiderTabItem = styled.button.attrs({ type: 'button' as const })<{
  $active: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 4px;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-family: 'Geist', sans-serif;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.01em;
  color: ${({ theme, $active }) =>
    $active ? r(theme).text.primary : r(theme).text.faint};
  position: relative;
  transition: color 160ms ease;

  & > svg, & > span[aria-hidden='true'] {
    font-size: 22px;
    line-height: 1;
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 18px;
        height: 2px;
        border-radius: 2px;
        background: ${r(theme).accent.lime};
        box-shadow: 0 0 6px ${r(theme).accent.lime};
      }
    `}

  &:hover {
    color: ${({ theme }) => r(theme).text.primary};
  }
`;
